import React from 'react';
import PropTypes from 'prop-types';
import { Popover, Tooltip } from 'antd';
import { formatRelativeTime } from '../../utils/relativeTime';
import './DataTable.css';

/**
 * A long list in a table cell: the first `max` items as chips (each cut to the cell, its whole text on hover) and
 * "+N", whose popover lists every item. `items`: `[{ key, label, title?, detail? }]` or strings; `label` may be a
 * node (a link), then `title` is its hover text; `detail` is a second line in the popover's list.
 */
export function ChipList({ items, max = 2, title, listLabel, none = null }) {
  const list = (items || []).map((item) => (typeof item === 'string' ? { key: item, label: item } : item));
  if (!list.length) { return none; }
  const rest = list.length - max;
  return (
    <span className="dt-chips">
      {list.slice(0, max).map((item) => <span className="dt-chip" key={item.key} title={item.title || (typeof item.label === 'string' ? item.label : undefined)}>{item.label}</span>)}
      {rest > 0 && (
        <Popover
          content={(
            <ul aria-label={listLabel || title || 'All'} className="dt-chip-list">
              {list.map((item) => <li key={item.key}>{item.label}{item.detail ? <code> {item.detail}</code> : null}</li>)}
            </ul>
          )}
          placement="bottomLeft"
          title={title}
          trigger="click"
        >
          <button aria-label={`${rest} more: show all ${list.length}`} className="dt-more-chip" type="button">+{rest}</button>
        </Popover>
      )}
    </span>
  );
}

ChipList.propTypes = {
  items: PropTypes.array,
  listLabel: PropTypes.string,
  max: PropTypes.number,
  none: PropTypes.node,
  title: PropTypes.string,
};

/** "When" then "by <who>" on its own line, each cut to the cell with the whole value on hover. */
export function WhenBy({ at, by, text }) {
  const when = text || (at ? formatRelativeTime(at) : '');
  const full = at ? new Date(at).toLocaleString() : undefined;
  return (
    <span className="dt-when">
      <span title={full}>{when || '—'}</span>
      {by && <span className="dt-when-by" title={by}>by {by}</span>}
    </span>
  );
}

WhenBy.propTypes = { at: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]), by: PropTypes.string, text: PropTypes.string };

/**
 * A table row's action as an icon with a tooltip (the column stays narrow): `label` is its accessible name, `tip` the
 * tooltip (default: the label). Other props go to the <button> (className, disabled, onClick…).
 */
export function IconButton({ icon, label, tip, ...rest }) {
  return (
    <Tooltip title={tip || label}>
      <button aria-label={label} type="button" {...rest}>{icon}</button>
    </Tooltip>
  );
}

IconButton.propTypes = { icon: PropTypes.node.isRequired, label: PropTypes.string.isRequired, tip: PropTypes.string };
